/**
 * AI Insight (Beta): the CrewAI Insight agent that answers data questions through cty-mcp-server
 * and cty-ai-data-access, so the user's DOC permissions apply to every number it returns.
 *
 * The page and its nav item show only when CTY_INSIGHT_AGENT_URL is set at build time (dev today)
 * and never in production builds.
 */
import type { AgentConfig, InsightCatalog } from './insightAgents/agentConfig';
import {
  MAX_REFRESH_WIDGETS,
  type DashboardFilters,
  type DashboardRequest,
  type DashboardUpdate,
  type InsightWidget,
  type WidgetDefinition,
  type WidgetRefresh,
} from './dashboard/widgets';

/** `filters` for a request body: sent when the dashboard has them, left out for one from before filters. */
const withFilters = (filters: DashboardFilters | null | undefined) => (filters === undefined ? {} : { filters: filters ?? {} });

/** AI Insight is the Analyst Agent tab of the Analysts page (/analytics?tab=analyst-agent). */
export const ANALYSTS_PATH = '/analytics';
export const ANALYST_AGENT_TAB = 'analyst-agent';
/** Its own pages (a saved dashboard, the agent editor) live under this path. */
export const AI_INSIGHT_PATH = `${ANALYSTS_PATH}/${ANALYST_AGENT_TAB}`;
/** Where AI Insight lived before it moved into Analysts; old links redirect (routes.tsx). */
export const LEGACY_AI_INSIGHT_PATH = '/ai-insight';

/** The default agent: answers any question when no other agent is chosen. */
export const DEFAULT_AGENT_NAME = 'Insight Agent';
/**
 * Its aiworker code, as the catalog's `default_agent_code` gives it. Only the Analysts list uses
 * this constant (to list the default once without loading the catalog); AI Insight reads the catalog.
 */
export const DEFAULT_AGENT_CODE = 'insightagent';
export const DEFAULT_AGENT_DESCRIPTION =
  'Answers any question across your data. Runs on the built-in settings until you customise it.';
export const AI_INSIGHT_TABS = {
  CONVERSATION: 'conversation',
  DATA_SOURCES: 'data-sources',
  CONFIGURATION: 'configuration',
  DASHBOARDS: 'dashboards',
} as const;
export type AIInsightTab = typeof AI_INSIGHT_TABS[keyof typeof AI_INSIGHT_TABS];

/**
 * `/analytics?tab=analyst-agent&section=…` plus any other query parameters (agent, view). The
 * Analysts page owns ?tab=, so AI Insight's own tab is ?section=.
 */
export function aiInsightUrl(tab: AIInsightTab, params: Record<string, string> = {}): string {
  const query = new URLSearchParams({ tab: ANALYST_AGENT_TAB, section: tab });
  for (const [key, value] of Object.entries(params)) {
    if (!query.has(key)) query.set(key, value);
  }
  return `${ANALYSTS_PATH}?${query.toString()}`;
}

/** A saved dashboard: /analytics/analyst-agent/dashboards/:configId. */
export const dashboardUrl = (configId: string) => `${AI_INSIGHT_PATH}/dashboards/${configId}`;

/** The agent editor: /analytics/analyst-agent/agents/:mode/:aiworker_id? (mode: create | edit | view). */
export function agentEditorUrl(mode: 'create' | 'edit' | 'view', aiworkerId?: string): string {
  return `${AI_INSIGHT_PATH}/agents/${mode}${aiworkerId ? `/${aiworkerId}` : ''}`;
}

/** The editor's create mode for the tenant's own Insight Agent (sent with the catalog's code). */
export const CUSTOMISE_DEFAULT_URL = `${agentEditorUrl('create')}?as=default`;

export function getInsightAgentUrl(): string {
  if (import.meta.env.CTY_MODE === 'production') return '';
  return (import.meta.env.CTY_INSIGHT_AGENT_URL ?? '').trim().replace(/\/+$/, '');
}

export interface InsightToolCall {
  tool: string;
  arguments: Record<string, unknown>;
  result?: string;
  error?: string;
  ms?: number;
}

/**
 * Which agent answered. id and version_id are null when Insight Agent ran on its built-in settings
 * (no customised Insight Agent saved yet) and for the editor's preview.
 */
export interface InsightAgentRef {
  id: string | null;
  name: string;
  version_id: string | null;
}

export interface InsightAnswer {
  answer: string;
  tool_calls?: InsightToolCall[];
  agent?: InsightAgentRef;
  warnings?: string[];
  /** Charts, tiles and tables for the session's dashboard; may be empty. */
  widgets?: InsightWidget[];
  /** Dashboard mode: the whole dashboard after this request; null when it could not be made. */
  dashboard?: DashboardUpdate | null;
  /** Mode "auto": how the service's query router routed this request. Absent where the router is off. */
  route?: InsightRoute;
  /** The clarify route: interpretations to offer the user (may be empty). */
  clarify?: { options: string[] };
  /** Asked before replacing an open dashboard: re-send the request with the route the user picks. */
  confirm?: { options: ConfirmOption[] };
}

/** One answer to "add to this dashboard, or start a new one?" */
export interface ConfirmOption {
  route: 'new_dashboard' | 'refine_dashboard';
  label: string;
}

/**
 * The query router's decision (mode "auto"): a new dashboard, a change or a filter on this one, an
 * answer in chat only (ad_hoc), a question back (clarify), or a fallback to dashboard mode.
 */
export interface InsightRoute {
  name: 'new_dashboard' | 'refine_dashboard' | 'filter_dashboard' | 'ad_hoc' | 'clarify' | 'fallback';
  source: string;
  confidence: number;
  ms: number;
  model?: string;
  reason?: string;
}

/** One step of a streamed run: a tool call (named by what it reads) or the dashboard step. */
export interface ProgressEvent {
  id: number | string;
  state: 'running' | 'done' | 'failed';
  label: string;
  ms?: number;
}

/** One earlier question and answer, oldest first. */
export interface InsightTurn {
  question: string;
  answer: string;
}

export const HISTORY_TURNS = 3;
const MAX_HISTORY_ANSWER = 2_000; // DESIGN.md §8: each earlier answer is trimmed

/** The last `limit` answered turns of a conversation (oldest first), each answer trimmed. */
export function recentHistory(turns: InsightTurn[], limit = HISTORY_TURNS): InsightTurn[] {
  return turns
    .slice(-limit)
    .map((turn) => ({ question: turn.question, answer: turn.answer.slice(0, MAX_HISTORY_ANSWER) }));
}

const ERROR_HINTS: Record<number, string> = {
  401: 'Your session could not be verified. Refresh the page to sign in again.',
  429: 'The Insight Agent is busy with other questions. Try again in a minute.',
};

/**
 * What fetch's own "Failed to fetch" means here: the service did not answer in a way the browser
 * could read, e.g. its load balancer had no running service behind it (503 without CORS headers).
 */
export const UNREACHABLE = 'The Insight Agent service can’t be reached right now. It may be restarting or stopped; try again in a few minutes.';

/** fetch, with a network failure (not a user's abort) turned into an InsightAgentError with status 0. */
async function reach(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new InsightAgentError(UNREACHABLE, 0);
  }
}

/** Thrown for a non-2xx answer; `status` lets callers tell 404 / 422 / 502 apart (0: not reached). */
export class InsightAgentError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function call<T>(url: string, token: string, init: RequestInit = {}, hints = ERROR_HINTS): Promise<T> {
  const response = await reach(url, {
    ...init,
    headers: { ...(init.body ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${token}` },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    // 422 names the config field that is wrong and 502 says whether DOC or the LLM failed: show them as is.
    const detail = typeof body?.detail === 'string' ? body.detail : `HTTP ${response.status}`;
    throw new InsightAgentError(hints[response.status] ?? detail, response.status);
  }
  return body as T;
}

/** No line from the stream for this long means the connection died: the service pings every 15 s. */
export const STREAM_IDLE_MS = 90_000;

/**
 * Mode "auto", streamed: the service's query router decides whether the request builds a new
 * dashboard, changes or filters this one, is answered in chat only, or needs a question back
 * (`route` in the result). Where the router is off, "auto" is dashboard mode: the request builds or
 * changes the conversation's one dashboard. The
 * service answers NDJSON lines (started, progress, ping, then result or error); `onProgress` hears
 * each step and the promise resolves with the result. Auth, agent and capacity errors arrive as
 * plain HTTP errors before the stream starts. A run may take minutes, so it is never cut off for
 * taking long, only when the stream goes quiet for STREAM_IDLE_MS.
 */
export async function askForDashboard(
  baseUrl: string,
  token: string,
  question: string,
  signal: AbortSignal | undefined,
  options: {
    agentId?: string;
    history?: InsightTurn[];
    dashboard: DashboardRequest;
    /** The filter bar's filters. Undefined: the service uses the dashboard's own (its widgets' snapshot). */
    filters?: DashboardFilters | null;
    onProgress?: (event: ProgressEvent) => void;
    /** The route the user chose in answer to `confirm`; the service does not ask its router again. */
    route?: ConfirmOption['route'];
  }
): Promise<InsightAnswer> {
  const { agentId, history = [], dashboard, filters, onProgress, route } = options;
  const controller = new AbortController();
  const stop = () => controller.abort();
  if (signal?.aborted) stop();
  else signal?.addEventListener('abort', stop, { once: true });
  let quiet = false;
  let idle: ReturnType<typeof setTimeout> | undefined;
  const heard = () => {
    clearTimeout(idle);
    idle = setTimeout(() => {
      quiet = true;
      controller.abort();
    }, STREAM_IDLE_MS);
  };
  heard();
  try {
    return await readDashboardStream(baseUrl, token, question, controller.signal, heard,
      { agentId, history, dashboard, filters, onProgress, route });
  } catch (error) {
    if (quiet) throw new InsightAgentError('The Insight Agent stopped responding. Try again.', 504);
    throw error;
  } finally {
    clearTimeout(idle);
    signal?.removeEventListener('abort', stop);
  }
}

/** The stream request's body: only the fields that are set. */
function streamBody(question: string, { agentId, history, dashboard, filters, route }: {
  agentId?: string;
  history: InsightTurn[];
  dashboard: DashboardRequest;
  filters?: DashboardFilters | null;
  route?: ConfirmOption['route'];
}): Record<string, unknown> {
  const body: Record<string, unknown> = { question, mode: 'auto' };
  if (route) body.route = route;
  if (agentId) body.agent_id = agentId;
  if (history.length) body.history = history;
  if (dashboard.widgets.length) body.dashboard = dashboard;
  return { ...body, ...withFilters(filters) };
}

async function readDashboardStream(
  baseUrl: string,
  token: string,
  question: string,
  signal: AbortSignal,
  heard: () => void,
  { agentId, history, dashboard, filters, onProgress, route }: {
    agentId?: string;
    history: InsightTurn[];
    dashboard: DashboardRequest;
    filters?: DashboardFilters | null;
    onProgress?: (event: ProgressEvent) => void;
    route?: ConfirmOption['route'];
  }
): Promise<InsightAnswer> {
  const response = await reach(`${baseUrl}/insight-agent/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(streamBody(question, { agentId, history, dashboard, filters, route })),
    signal,
  });
  if (!response.ok || !response.body) {
    const body = await response.json().catch(() => ({}));
    const hints: Record<number, string> = { ...ERROR_HINTS, 404: 'This Insight agent is not available to you. Pick another agent.' };
    const detail = typeof body?.detail === 'string' ? body.detail : `HTTP ${response.status}`;
    throw new InsightAgentError(hints[response.status] ?? detail, response.status);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  const handle = (line: string): InsightAnswer | undefined => {
    if (!line.trim()) return undefined;
    const event = JSON.parse(line);
    if (event.type === 'progress') onProgress?.(event as ProgressEvent);
    if (event.type === 'error') throw new InsightAgentError(String(event.detail ?? 'The run failed.'), Number(event.status) || 502);
    return event.type === 'result' ? (event.data as InsightAnswer) : undefined;
  };
  for (;;) {
    const { value, done } = await reader.read();
    heard();
    buffered += decoder.decode(value, { stream: !done });
    const lines = buffered.split('\n');
    buffered = done ? '' : lines.pop() ?? '';
    for (const line of lines) {
      const result = handle(line);
      if (result) return result;
    }
    if (done) throw new InsightAgentError('The Insight Agent stopped without an answer.', 502);
  }
}

/** The editor's Test tab: runs an unsaved config with the same validation and fixed rules. Stores nothing. */
export async function previewInsightAgent(
  baseUrl: string,
  token: string,
  body: { question: string; config: AgentConfig; history?: InsightTurn[] },
  signal?: AbortSignal
): Promise<InsightAnswer> {
  const { history = [], ...rest } = body;
  return call<InsightAnswer>(`${baseUrl}/insight-agent/preview`, token, {
    method: 'POST',
    body: JSON.stringify({ ...rest, ...(history.length ? { history } : {}) }),
    signal,
  });
}

/** Models, fixed rules, built-in guidance and the default config, for the editor. */
export async function getInsightCatalog(baseUrl: string, token: string, signal?: AbortSignal): Promise<InsightCatalog> {
  return call<InsightCatalog>(`${baseUrl}/insight-agent/catalog`, token, { signal });
}

export type ColumnTreatment = 'allow' | 'mask' | 'partial' | 'pseudonymize' | string;

export interface DataAccessColumn {
  name: string;
  type: string;
  shown_as: ColumnTreatment;
  description: string;
}

export interface DataAccessDataset {
  name: string;
  description: string;
  row_scope: string;
  time_column: string;
  columns: DataAccessColumn[];
  hidden: string[];
  metrics: string[];
  error?: string;
}

export interface DataAccessView {
  datasets: DataAccessDataset[];
  error?: string;
}

/** What the Insight Agent may read for this user: their DOC access intersected with the agent's. */
export async function getDataAccess(baseUrl: string, token: string, signal?: AbortSignal): Promise<DataAccessView> {
  return call<DataAccessView>(`${baseUrl}/insight-agent/access`, token, { signal });
}

export type RefreshResult = WidgetRefresh;

/**
 * Re-runs saved widgets' queries as the signed-in viewer (no LLM), MAX_REFRESH_WIDGETS per call.
 * A failed batch marks its widgets with the error; the others still refresh.
 */
export async function refreshWidgets(
  baseUrl: string,
  token: string,
  agentId: string | null,
  widgets: WidgetDefinition[],
  signal?: AbortSignal,
  /** The filter bar's filters. Undefined: each widget re-applies the filters it was built with. */
  filters?: DashboardFilters | null
): Promise<RefreshResult[]> {
  const batches: WidgetDefinition[][] = [];
  for (let i = 0; i < widgets.length; i += MAX_REFRESH_WIDGETS) batches.push(widgets.slice(i, i + MAX_REFRESH_WIDGETS));
  const results = await Promise.all(batches.map(async (batch) => {
    try {
      const body = await call<{ widgets?: RefreshResult[] }>(`${baseUrl}/insight-agent/dashboards/refresh`, token, {
        method: 'POST',
        body: JSON.stringify({ agent_id: agentId || null, widgets: batch, ...withFilters(filters) }),
        signal,
      });
      return Array.isArray(body.widgets) ? body.widgets : [];
    } catch (error) {
      if (signal?.aborted) throw error;
      return batch.map((widget) => ({ id: widget.id, error: (error as Error).message }));
    }
  }));
  return results.flat();
}

/** The rows behind one node of a decomposition tree (POST /insight-agent/dashboards/rows, AI-9683). */
export interface TreeRows {
  /** The row query the service ran (as the viewer, with the dashboard's filters). */
  sql: string;
  columns: Array<{ field: string; label: string; type?: 'number' | 'text' }>;
  rows: Record<string, unknown>[];
  /** More rows matched than the 200 shown. */
  truncated: boolean;
}

export interface TreeRowsRequest {
  agentId: string | null;
  /** The tree widget's query. */
  source: { tool: string; arguments: Record<string, unknown> };
  /** Its levels (config.hierarchyFields), outermost first, and the chosen node's value at each. */
  levels: string[];
  path: string[];
  filters?: DashboardFilters | null;
}

export function readTreeRows(baseUrl: string, token: string, request: TreeRowsRequest, signal?: AbortSignal): Promise<TreeRows> {
  return call<TreeRows>(`${baseUrl}/insight-agent/dashboards/rows`, token, {
    method: 'POST',
    body: JSON.stringify({
      agent_id: request.agentId || null,
      source: { tool: request.source.tool, arguments: request.source.arguments },
      levels: request.levels,
      path: request.path,
      ...withFilters(request.filters),
    }),
    signal,
  });
}

/** A data query an answer ran, as "Add to dashboard" sends it back to be re-run and charted. */
export interface DataSource {
  tool: 'run_sql' | 'query_metric';
  arguments: Record<string, unknown>;
}

export const MAX_VISUALIZE_SOURCES = 10;
const MAX_VISUALIZE_ANSWER = 20_000;

/** The governed queries a turn ran (run_sql, query_metric; failed ones left out), in order, each once. */
export function dataSources(calls: InsightToolCall[] | undefined): DataSource[] {
  const seen = new Set<string>();
  const sources: DataSource[] = [];
  for (const call of calls ?? []) {
    if ((call.tool !== 'run_sql' && call.tool !== 'query_metric') || call.error) continue;
    const args = call.arguments;
    if (!args || typeof args !== 'object' || Object.keys(args).length === 0) continue;
    const key = `${call.tool}:${stableJson(args)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    sources.push({ tool: call.tool, arguments: args });
  }
  return sources.slice(0, MAX_VISUALIZE_SOURCES);
}

/**
 * JSON with object keys sorted, so the same value with its keys in another order compares equal: the
 * same query arguments, or a saved dashboard that DOC's JSONB storage returns in its own key order.
 * Like JSON.stringify, it leaves out keys whose value is undefined.
 */
export function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).filter((key) => record[key] !== undefined).sort((a, b) => a.localeCompare(b))
      .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * "Add to dashboard" for an answer that came without widgets: the runtime re-runs its queries as the
 * signed-in user (within that agent's data sources) and charts them. Takes 5-20 s.
 */
export async function visualizeAnswer(
  baseUrl: string,
  token: string,
  body: { agentId: string | null; question: string; answer: string; sources: DataSource[]; filters?: DashboardFilters | null },
  signal?: AbortSignal
): Promise<{ widgets?: InsightWidget[]; warnings?: string[] }> {
  return call(`${baseUrl}/insight-agent/dashboards/visualize`, token, {
    method: 'POST',
    body: JSON.stringify({
      agent_id: body.agentId || null,
      question: body.question,
      answer: body.answer.slice(0, MAX_VISUALIZE_ANSWER),
      sources: body.sources.slice(0, MAX_VISUALIZE_SOURCES),
      ...withFilters(body.filters),
    }),
    signal,
  });
}
